import { AGREEMENT_STORAGE_KEY } from "@/constants/consent.constants";
import { useCallback, useMemo, useState } from "react";

/**
 * Consent payload structure stored in localStorage
 */
type ConsentPayload = {
  /** Whether user has agreed */
  agreed: boolean;
  /** Agreement version */
  version: string;
  /** ISO timestamp of agreement */
  at: string;
  /** Optional user ID */
  userId?: string;
};

/**
 * useAgreementConsent hook manages user agreement/consent state in localStorage.
 * Provides methods to check, set, and reset consent status with version tracking.
 * 
 * @hook
 * @returns {Object} Consent state and methods
 * @returns {boolean} returns.isAgreed - Whether user has agreed to current version
 * @returns {Function} returns.agree - Function to record agreement (version, agreed, userId?)
 * @returns {Function} returns.reset - Function to clear consent from storage
 * 
 * @example
 * ```tsx
 * const { isAgreed, agree, reset } = useAgreementConsent();
 * 
 * if (!isAgreed) {
 *   return <ConsentModal onAgree={() => agree('v1.0', true)} />;
 * }
 * ```
 */
export const useAgreementConsent = () => {
  const read = (): boolean => {
    try {
      const raw = localStorage.getItem(AGREEMENT_STORAGE_KEY);
      if (!raw) return false;
      const parsed: ConsentPayload = JSON.parse(raw);
      return parsed?.agreed === true && !!parsed?.version;
    } catch {
      return false;
    }
  };

  const [isAgreed, setIsAgreed] = useState<boolean>(read);

  const agree = useCallback((agreementVersion: string, agreed: boolean, userId?: string) => {
    const payload: ConsentPayload = {
      agreed: agreed,
      version: agreementVersion,
      at: new Date().toISOString(),
      userId,
    };
    localStorage.setItem(AGREEMENT_STORAGE_KEY, JSON.stringify(payload));
    setIsAgreed(true);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(AGREEMENT_STORAGE_KEY);
    setIsAgreed(false);
  }, []);

  return useMemo(() => ({ isAgreed, agree, reset }), [isAgreed, agree, reset]);
};